import { SupabaseClient } from '@supabase/supabase-js';
import { BaseRepository } from './base.repository';
import {
  ChangeProposal,
  ChangeProposalInsert,
  ChangeProposalUpdate,
  ChangeProposalDocument,
  ChangeProposalDocumentInsert
} from '../entities/change-proposal';

export class ChangeProposalRepository extends BaseRepository<ChangeProposal, ChangeProposalInsert, ChangeProposalUpdate> {
  constructor(supabase: SupabaseClient) {
    super(supabase, 'change_proposals');
  }

  /**
   * Save a ChangeProposal entity (create or update based on dirty state)
   */
  async save(proposal: ChangeProposal): Promise<ChangeProposal> {
    if (!proposal.isDirty()) {
      return proposal; // No changes to save
    }

    const data = proposal.toData();
    const { id, created_at, ...updateData } = data;

    const { data: saved, error } = await this.supabase
      .from(this.tableName)
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Create new entity instance from saved data
    const savedProposal = ChangeProposal.fromData(saved);
    savedProposal.markAsClean();
    return savedProposal;
  }

  /**
   * Override findById to return ChangeProposal entity
   */
  async findById(id: string): Promise<ChangeProposal | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }

    return ChangeProposal.fromData(data);
  }

  /**
   * Get proposals by project ID
   */
  async findByProjectId(projectId: string): Promise<ChangeProposal[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data.map(item => ChangeProposal.fromData(item));
  }

  /**
   * Get proposals by status
   */
  async findByStatus(projectId: string, status: 'pending' | 'approved' | 'rejected'): Promise<ChangeProposal[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .eq('status', status)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data.map(item => ChangeProposal.fromData(item));
  }

  /**
   * Get proposals by chat ID
   */
  async findByChatId(chatId: string): Promise<ChangeProposal[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('chat_id', chatId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data.map(item => ChangeProposal.fromData(item));
  }

  /**
   * Get proposal with documents
   */
  async findByIdWithDocuments(id: string): Promise<{ proposal: ChangeProposal; documents: Array<ChangeProposalDocument & { documentTitle?: string }> } | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select(`
        *,
        change_proposal_documents (
          *,
          document:documents(title)
        )
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }

    const proposal = ChangeProposal.fromData(data);
    const documents = data.change_proposal_documents?.map((doc: any) => {
      // Extract document title before creating entity
      const documentTitle = doc.document?.title;

      // Remove joined data before creating entity
      const { document, ...docData } = doc;

      const proposalDoc = ChangeProposalDocument.fromData(docData);
      // Add document title from join
      (proposalDoc as any).documentTitle = documentTitle;
      return proposalDoc as ChangeProposalDocument & { documentTitle?: string };
    }) || [];

    return { proposal, documents };
  }
}

export class ChangeProposalDocumentRepository extends BaseRepository<ChangeProposalDocument, ChangeProposalDocumentInsert, any> {
  constructor(supabase: SupabaseClient) {
    super(supabase, 'change_proposal_documents');
  }

  /**
   * Override findById to return ChangeProposalDocument entity
   */
  async findById(id: string): Promise<ChangeProposalDocument | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }

    return ChangeProposalDocument.fromData(data);
  }

  /**
   * Get documents by proposal ID
   */
  async findByProposalId(proposalId: string): Promise<ChangeProposalDocument[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('change_proposal_id', proposalId)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return data.map(item => ChangeProposalDocument.fromData(item));
  }

  /**
   * Create multiple proposal documents in a transaction
   */
  async createMultiple(documents: ChangeProposalDocumentInsert[]): Promise<ChangeProposalDocument[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .insert(documents)
      .select();

    if (error) throw error;

    return data.map(item => ChangeProposalDocument.fromData(item));
  }

  /**
   * Delete all documents for a proposal
   */
  async deleteByProposalId(proposalId: string): Promise<void> {
    const { error } = await this.supabase
      .from(this.tableName)
      .delete()
      .eq('change_proposal_id', proposalId);

    if (error) throw error;
  }
}